import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { reportAdProductShortLabel } from '@/api/reportAdProducts';
import { formatIsoDisplayDate, isSponsoredProducts, toShortAdProduct } from '@/api/ruleRecommendations';
import type { CampaignAdProduct } from '@/types/spCampaigns';

const SP_ONLY_REASON = 'Actions are available for Sponsored Products only.';

export function ruleCampaignDetailPath(
  profileId: string,
  campaignId: string,
  adProduct: CampaignAdProduct
): string {
  const base = `/accounts/${encodeURIComponent(profileId)}/campaigns/${encodeURIComponent(campaignId)}`;
  if (adProduct !== 'SP') return `${base}?product=${encodeURIComponent(adProduct)}`;
  return base;
}

export function formatRatio(value: number | null | undefined, digits = 2): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value);
}

export function formatPercent(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return `${value.toFixed(1)}%`;
}

export function CampaignNameCell({
  profileId,
  campaignId,
  campaignName,
  adProduct,
  campaignType,
}: {
  profileId: string;
  campaignId: string;
  campaignName: string;
  adProduct?: string;
  campaignType?: string;
}) {
  const short = toShortAdProduct(adProduct, campaignType);
  return (
    <Link
      to={ruleCampaignDetailPath(profileId, campaignId, short)}
      className="font-medium text-brand hover:underline"
    >
      {campaignName}
    </Link>
  );
}

export function AdTypeCell({ adProduct, campaignType }: { adProduct?: string; campaignType?: string }) {
  return (
    <span className="font-mono text-body-sm">
      {reportAdProductShortLabel(adProduct || campaignType || 'SP')}
    </span>
  );
}

export function DateCell({ value, empty = 'No end date' }: { value: string | null | undefined; empty?: string }) {
  return <span>{formatIsoDisplayDate(value, empty)}</span>;
}

export function SpActionButton({
  adProduct,
  campaignType,
  children,
  onClick,
  disabled,
}: {
  adProduct?: string;
  campaignType?: string;
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
}) {
  const allowed = isSponsoredProducts(adProduct, campaignType);
  return (
    <Button
      type="button"
      size="sm"
      variant="secondary"
      onClick={onClick}
      disabled={disabled || !allowed}
      title={allowed ? undefined : SP_ONLY_REASON}
    >
      {children}
    </Button>
  );
}

export function DismissButton({ onClick }: { onClick: () => void }) {
  return (
    <Button type="button" size="sm" variant="ghost" onClick={onClick}>
      Dismiss
    </Button>
  );
}

export function EmptyTableMessage({ message }: { message: string }) {
  return <p className="px-4 py-6 text-sm text-foreground-subtle">{message}</p>;
}
